// src/oura/components/newscan/DecisionList.tsx
//
// What staff settle before the 50 can be approved, decisions first:
//   1. the outcome line: "3 fixed. Last: Price set to RM120 (your
//      choice) · Undo", on top, so Undo sits where the fixed card was (the
//      next card takes its place under it). Each answer also offers Undo in
//      a snackbar, for staff who have scrolled on. The line's Undo always
//      names and undoes the latest change. The snackbar's Undo is bound to the
//      draft revision its answer produced: pressed after a newer change, it
//      refuses with a sentence instead of undoing that newer change;
//   2. the open blocker and decide cards, worst first (a recipe change, when
//      the parent passes its card, leads);
//   3. Search Console suggestions under "From Search Console · optional",
//      blue-tinted, white buttons only;
//   4. notes, folded into one quiet line: "2 notes", or the note's own title
//      when there is one ("Oura reworded 3 questions that repeated others").
//      A note whose detail has several lines (auto_reworded: "17. <new
//      wording> (was: <old wording>)" per row) opens to that list.
//
// A server-side fix is one write. A client-side option (open_editor,
// open_word, connect_gsc, choose_each) only moves the screen.
//
// Smoothness: when a fix lands, the card leaves without the page jumping
// (the next card is put exactly where the fixed one was) and focus moves to
// that next card, or to the outcome line when none is left. Undo does the
// same: focus goes to the card it brought back, or stays on the outcome line.
// When the recipe card resolves, focus goes to the first open card.
//
// `readOnly` (an approved or started draft): the cards are shown without
// their buttons and the outcome line without Undo.
//
// First AI check (JOURNEY-SPEC §2.5): the form and quote cards come here with
// their "From Sarah's form" line; "Name the area", "Add an outlet" and
// "Choose the 9" open the Business step (`onOpenBusiness`). The "For the
// plan" cards are not listed here: the Review shows them after these, under
// their own label (PlanDecisions). The pitch line is never a note.
//
// Brand mode (v4.5): its decisions arrive with their own titles and fixes and
// use the same cards. The "no Google search behind them" note carries a link
// to the Searches view, where those questions are listed.

import { ChevronRightIcon, InfoIcon, SearchIcon } from "lucide-react";
import { useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
    CLIENT_SIDE_ACTIONS,
    type DecisionChoice,
    type DecisionOption,
    type ScanDraft,
    type ScanDraftDecision,
    type ScanDraftWord,
} from "@service/aeo/scanDraftTypes";
import { useSnackbar } from "@hook/use-snackbar";
import { cn } from "@oura/lib/cn";
import { FOCUS_RING, PillButton, TYPE } from "@oura/ui";
import { useScanFlow } from "@oura/newscan/ScanFlowContext";
import { UNDO_MOVED_ON, type DraftRevision } from "@oura/newscan/useScanDraft";
import { DECISION, FRAME, OUTCOME, plural } from "@oura/newscan/copy";
import { DecisionCard, DraftDecisionCard } from "./DecisionCard";
import { openDecisions, openGates } from "./openDecisions";
import { OutcomeLine } from "./OutcomeLine";
import { isPlanCard } from "./firstCheckFacts";

const COPY = {
    label: "Decisions",
    fromSearch: "From Search Console",
    optional: "· optional",
    notes: (n: number) => plural(n, "note"),
    openSearches: "Open Searches",
} as const;

/**
 * A fix names its result, so a label can quote a whole question ("Use “Which
 * physio clinic in Subang Jaya should I go to …”"). The kit's pills never
 * wrap; on a phone that pushed the page sideways. Here a long label wraps
 * inside its pill instead, at the pill's own height when it fits.
 */
const WRAP_PILLS =
    "[&_button]:h-auto [&_button]:min-h-7 [&_button]:max-w-full [&_button]:whitespace-normal [&_button]:py-1 [&_button]:text-left max-sm:[&_button]:min-h-9";

/** A fix that has not landed within this long no longer moves the page. */
const ANCHOR_MS = 4000;

/** How long the snackbar's Undo stays up after an answer. */
const UNDO_TOAST_MS = 6000;

/** Focus a target when focus has been lost (a disabled or removed button left it on the body). */
function focusIfLost(target: HTMLElement | null | undefined) {
    const a = document.activeElement;
    if (target && (!a || a === document.body)) target.focus({ preventScroll: true });
}

const stepTitle = () => document.querySelector<HTMLElement>("[data-step-title]");

const sel = (id: string) => `[data-decision="${CSS.escape(id)}"]`;

export interface DecisionListProps {
    draft: ScanDraft;
    /** Rendered first, in place of the recipe_change decision (RecipeChangeCard). */
    recipeCard?: ReactNode;
    onOpenEditor(n: number): void;
    onOpenWord(variable: string): void;
    /** "Choose each" on a recipe change shown as a plain card (the recipe card handles its own). */
    onChooseEach?(): void;
    /** An approved or started draft: no buttons on the cards, no Undo. */
    readOnly?: boolean;
    /** Brand mode: shows the Searches view (the untraced questions' note links to it). */
    onOpenSearches?(): void;
    /** First AI check: "Name the area", "Add an outlet", "Choose the 9" open the Business step. */
    /** `area`: "Name the area" opens the outlet field with the form's area in it. */
    onOpenBusiness?(area?: string): void;
    /** The branches list says its own outcome and Undo: no second line here for its writes. */
    hideOutcome?: boolean;
}

export function DecisionList({
    draft,
    recipeCard,
    onOpenEditor,
    onOpenWord,
    onChooseEach,
    readOnly = false,
    onOpenSearches,
    onOpenBusiness,
    hideOutcome = false,
}: DecisionListProps) {
    const { ctl, connectGsc } = useScanFlow();
    const { showSuccess, showError } = useSnackbar();
    const [pending, setPending] = useState<{ id: string; key: string } | null>(null);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [notesOpen, setNotesOpen] = useState(false);
    const outcomeRef = useRef<HTMLDivElement>(null);
    /** The fixed card's place, so the next one can take it. */
    const anchor = useRef<{ id: string; index: number; top: number; at: number } | null>(null);
    /** The cards shown when Undo was pressed, so the one it brings back can take focus. */
    const undoBefore = useRef<Set<string> | null>(null);
    const hadRecipe = useRef(Boolean(recipeCard));
    /** Read when an answer lands, which can be after this render: an approved draft raises no toast. */
    const readOnlyRef = useRef(readOnly);
    readOnlyRef.current = readOnly;
    const words = useMemo(() => {
        const map: Record<string, ScanDraftWord> = {};
        draft.words.forEach((w) => {
            map[w.variable] = w;
        });
        return map;
    }, [draft.words]);

    const gates = openGates(draft).filter((d) => !(recipeCard && d.type === "recipe_change") && !isPlanCard(d));
    // The form's goal question lives in "From the form" (FormAnswers), never with the Search Console ones.
    const suggestions = openDecisions(draft, ["suggestion"]).filter((d) => d.type !== "form_goal_core");
    const notes = openDecisions(draft, ["info"]);
    const shownIds = [...(recipeCard ? ["recipe_change"] : []), ...gates.map((d) => d.id)];
    const gateIds = shownIds.join("|");

    // After a fix lands: keep the page still and move focus on.
    useLayoutEffect(() => {
        const recipeWent = hadRecipe.current && !recipeCard;
        hadRecipe.current = Boolean(recipeCard);
        // Undo brought a card back: focus it.
        const before = undoBefore.current;
        if (before) {
            const back = shownIds.find((id) => !before.has(id));
            if (back) {
                undoBefore.current = null;
                const el = document.querySelector<HTMLElement>(sel(back));
                if (el) {
                    el.focus({ preventScroll: true });
                    const r = el.getBoundingClientRect();
                    if (r.top < 0 || r.bottom > window.innerHeight) el.scrollIntoView({ block: "nearest" });
                }
                return;
            }
        }
        // The recipe card resolved (Take all, Use these): its buttons are gone, so focus moves on.
        if (recipeWent) {
            focusIfLost(gates[0] ? document.querySelector<HTMLElement>(sel(gates[0].id)) : (outcomeRef.current ?? stepTitle()));
        }
        const a = anchor.current;
        if (a && Date.now() - a.at > ANCHOR_MS) anchor.current = null;
        if (!a || !anchor.current || gates.some((d) => d.id === a.id)) return;
        anchor.current = null;
        const next = gates[a.index] ?? gates[a.index - 1] ?? null;
        const target = next ? document.querySelector<HTMLElement>(sel(next.id)) : outcomeRef.current;
        if (!target) return;
        const delta = target.getBoundingClientRect().top - a.top;
        if (Math.abs(delta) > 1) window.scrollBy(0, delta);
        target.focus({ preventScroll: true });
        // gateIds names what matters: the list of open cards.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gateIds]);

    const pick = async (d: ScanDraftDecision, choice: DecisionChoice, option: DecisionOption, key: string, index: number) => {
        const action = option.action;
        if (CLIENT_SIDE_ACTIONS.includes(action.kind)) {
            if (action.kind === "open_editor") onOpenEditor(action.n);
            else if (action.kind === "open_word") onOpenWord(action.variable);
            else if (action.kind === "connect_gsc") void connectGsc();
            else if (action.kind === "choose_each") onChooseEach?.();
            // "Add an outlet" opens an empty outlet field (""), "Name the area" one with the area in it.
            else if (action.kind === "open_business") onOpenBusiness?.(action.area ?? "");
            else if (action.kind === "choose_outlets") onOpenBusiness?.();
            return;
        }
        if (ctl.busy) return;
        const card = document.querySelector<HTMLElement>(sel(d.id));
        anchor.current = index >= 0 && card ? { id: d.id, index, top: card.getBoundingClientRect().top, at: Date.now() } : null;
        setPending({ id: d.id, key });
        setErrors((was) => {
            if (!(d.id in was)) return was;
            const next = { ...was };
            delete next[d.id];
            return next;
        });
        // The toast is raised the moment the server accepts the answer, with the revision that answer
        // produced, so it shows every time and its Undo can only undo this answer.
        const fail = await ctl.answer(d.id, choice, ({ effect, draftId, version }) => {
            if (effect && !readOnlyRef.current) toastUndo(effect.label, { draftId, version });
        });
        setPending(null);
        // On success the new draft renders after this line runs: the effect above takes the anchor then.
        if (fail && anchor.current?.id === d.id) anchor.current = null;
        if (fail && fail.kind !== "stale") {
            setErrors((was) => ({ ...was, [d.id]: fail.kind === "network" ? FRAME.network : fail.message }));
        }
    };

    /** `expect`: the revision a snackbar was raised for (none from the outcome line, which is always the latest). */
    const runUndo = async (expect?: DraftRevision) => {
        undoBefore.current = new Set(shownIds);
        const fail = await ctl.undo(expect);
        if (fail?.code === UNDO_MOVED_ON.code) {
            undoBefore.current = null;
            showError("Not undone", UNDO_MOVED_ON.message);
            return;
        }
        // An undo that brings no card back (a word change, a failure): keep focus on the line, or the title once it is gone.
        requestAnimationFrame(() =>
            requestAnimationFrame(() => {
                if (!undoBefore.current) return;
                undoBefore.current = null;
                focusIfLost(outcomeRef.current ?? stepTitle());
            }),
        );
    };

    // The snackbar's Undo outlives this render: it calls the latest runUndo, with the revision it was raised for.
    const undoRef = useRef(runUndo);
    undoRef.current = runUndo;
    // Only an answer from a card here: rewords, locks and word edits say their outcome in the foot.
    const toastUndo = (label: string, revision: DraftRevision) => {
        showSuccess(label, undefined, UNDO_TOAST_MS, { actionLabel: OUTCOME.undo, onAction: () => void undoRef.current(revision) });
    };

    const card = (d: ScanDraftDecision, index: number, level: 2 | 3) => (
        <li
            key={d.id}
            data-decision={d.id}
            tabIndex={-1}
            className={cn(
                "rounded-2xl outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600",
                WRAP_PILLS,
            )}
        >
            {readOnly ? (
                <DecisionCard
                    severity={d.severity}
                    title={d.title}
                    detail={d.detail}
                    quotes={d.quotes}
                    evidence={d.evidence ? DECISION.evidence(d.evidence.query, d.evidence.searches) : undefined}
                    source={d.source || undefined}
                    origin={d.origin || undefined}
                    words={words}
                    level={level}
                />
            ) : (
                <DraftDecisionCard
                    decision={d}
                    level={level}
                    words={words}
                    pendingKey={pending?.id === d.id ? pending.key : null}
                    disabled={Boolean(ctl.busy) && pending?.id !== d.id}
                    onPick={(choice, option) => {
                        const key =
                            choice === "recommended"
                                ? "recommended"
                                : typeof choice === "object"
                                  ? `alt:${choice.alternative}`
                                  : String(choice);
                        void pick(d, choice, option, key, index);
                    }}
                />
            )}
            {errors[d.id] && (
                <p role="alert" className={cn(TYPE.tc, "m-0 mt-2 px-1 font-medium text-rose-600")}>
                    {errors[d.id]}
                </p>
            )}
        </li>
    );

    const undo = draft.undo;
    const showOutcome = !hideOutcome && undo.available > 0 && Boolean(undo.lastLabel);
    if (!recipeCard && gates.length === 0 && !showOutcome && suggestions.length === 0 && notes.length === 0) return null;

    return (
        <section aria-label={COPY.label} className="space-y-4">
            {showOutcome && (
                <div
                    ref={outcomeRef}
                    tabIndex={-1}
                    className="rounded-lg px-1 outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                >
                    <OutcomeLine
                        last={undo.lastLabel}
                        count={draft.decisionsAnswered}
                        onUndo={readOnly ? undefined : () => void runUndo()}
                        undoDisabled={Boolean(ctl.busy) && ctl.busy !== "Undoing"}
                        undoBusy={ctl.busy === "Undoing"}
                    />
                </div>
            )}

            {(recipeCard || gates.length > 0) && (
                <ul role="list" className="list-plain m-0 space-y-3 p-0">
                    {recipeCard && (
                        <li
                            data-decision="recipe_change"
                            tabIndex={-1}
                            className="rounded-2xl outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                        >
                            {recipeCard}
                        </li>
                    )}
                    {gates.map((d, i) => card(d, i, 2))}
                </ul>
            )}

            {suggestions.length > 0 && (
                <div className="space-y-2.5">
                    <h2 className={cn(TYPE.lab, "m-0 flex items-center gap-1.5")}>
                        <SearchIcon aria-hidden size={14} className="shrink-0 text-blue-600" />
                        {COPY.fromSearch}
                        <span className="font-normal text-gray-400">{COPY.optional}</span>
                    </h2>
                    <ul role="list" className="list-plain m-0 space-y-3 p-0">
                        {suggestions.map((d) => card(d, -1, 3))}
                    </ul>
                </div>
            )}

            {notes.length > 0 && (
                <div>
                    <button
                        type="button"
                        aria-expanded={notesOpen}
                        onClick={() => setNotesOpen((o) => !o)}
                        className={cn(
                            TYPE.tc,
                            "relative inline-flex items-center gap-1.5 rounded px-1 font-medium text-gray-500 hover:text-gray-900",
                            "after:absolute after:-inset-y-3 after:inset-x-0 after:content-[''] motion-safe:transition-colors",
                            FOCUS_RING,
                        )}
                    >
                        <InfoIcon aria-hidden size={14} className="shrink-0" />
                        {notes.length === 1 ? notes[0].title : COPY.notes(notes.length)}
                        <ChevronRightIcon
                            aria-hidden
                            size={14}
                            className={cn("shrink-0 motion-safe:transition-transform motion-safe:duration-200", notesOpen && "rotate-90")}
                        />
                    </button>
                    {notesOpen && (
                        <ul
                            role="list"
                            className="list-plain m-0 mt-2 divide-y divide-gray-100 rounded-2xl bg-white px-4 py-1 ring-1 ring-black/[0.04]"
                        >
                            {notes.map((d) => (
                                <li key={d.id} className="py-2.5">
                                    {/* One note: its title is already the line that opened it. */}
                                    {notes.length > 1 && <p className={cn(TYPE.tc, "m-0 mb-0.5 font-medium text-gray-900")}>{d.title}</p>}
                                    {d.detail && <NoteDetail detail={d.detail} />}
                                    {d.type === "untraced_row" && onOpenSearches && (
                                        <PillButton variant="text" onClick={onOpenSearches} className="mt-1">
                                            {COPY.openSearches}
                                        </PillButton>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </section>
    );
}

/** A note's detail: one line as a sentence, several lines as a list. */
function NoteDetail({ detail }: { detail: string }) {
    const lines = detail.split("\n").filter((l) => l.trim());
    if (lines.length < 2) return <p className={cn(TYPE.tc, "m-0 text-gray-500")}>{detail}</p>;
    return (
        <ul role="list" className="list-plain m-0 space-y-1 p-0">
            {lines.map((line) => (
                <li key={line} className={cn(TYPE.tc, "break-words text-gray-500")}>
                    {line}
                </li>
            ))}
        </ul>
    );
}
